import type { JSX } from 'react';
import { useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@lib/cn';

interface FAQItemProps {
  readonly question: string;
  readonly answer: string;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
}

/**
 * Primitive `FAQItem` — UI-03.
 *
 * Accordion accessivel (PRD §4.8 + §7.6 a11y). Cada item e composto de:
 *  - `<button>` trigger com `aria-expanded` + `aria-controls` apontando pro
 *    painel; Enter/Space disparam o toggle (comportamento nativo do button).
 *  - `<div role="region" aria-labelledby="...">` painel com a resposta.
 *  - `<ChevronDown>` icone que rotaciona 180deg quando aberto (`rotate-180`).
 *
 * Estado controlado pelo parent (`isOpen` + `onToggle`) — permite a Faq
 * section enforcar "apenas um aberto por vez" controlando qual indice esta
 * ativo (PRD §4.8).
 *
 * Acessibilidade:
 *  - Tab focus visivel via focus-visible:ring-brand-500 (mesmo padrao do Button)
 *  - Enter/Space toggle (nativo do button)
 *  - `aria-controls` linka button -> panel; `aria-labelledby` linka panel -> button
 *  - Painel collapsed usa `hidden` (display:none) — leitores de tela respeitam
 *    e o keyboard tab pula o conteudo escondido
 *  - Quando aberto, painel renderiza animado via `grid-template-rows`
 *    transition (CSS-only, sem framer-motion)
 *
 * IDs gerados via `useId` para garantir unicidade quando ha varios FAQItem
 * no mesmo documento — evita colisao se um futuro reuso colocar 2 FAQs.
 */
export const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps): JSX.Element => {
  const headerId = useId();
  const panelId = useId();

  return (
    <div className="border-b border-neutral-200 last:border-b-0">
      <h3 className="m-0">
        <button
          type="button"
          id={headerId}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-4 py-5 text-left outline-none transition-colors hover:text-brand-600 focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50 sm:py-6"
        >
          <span className="font-display text-base font-bold text-neutral-800 sm:text-lg">
            {question}
          </span>
          <ChevronDown
            className={cn(
              'h-5 w-5 shrink-0 text-brand-500 transition-transform duration-300',
              isOpen ? 'rotate-180' : 'rotate-0',
            )}
            aria-hidden="true"
          />
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        hidden={!isOpen}
        className="pb-6 pr-9 text-base leading-relaxed text-neutral-700"
      >
        {answer}
      </div>
    </div>
  );
};
